import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Minus } from "@phosphor-icons/react";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { numberToSpanishPesos } from "@/lib/numToWords";
import OficioUpload from "@/components/expediente/OficioUpload";

const OPERACIONES = [
  { v: "DESLINDE", label: "DESLINDE" },
  { v: "SUBDIVISION", label: "SUBDIVISIÓN" },
  { v: "REFUNDICION", label: "REFUNDICIÓN" },
  { v: "URBANIZACION PARCELARIA", label: "URBANIZACIÓN PARCELARIA" },
  { v: "DIVISION PARA CONSTITUCION DE CONDOMINIO", label: "DIVISIÓN PARA CONSTITUCIÓN DE CONDOMINIO" },
  { v: "SANEAMIENTO", label: "SANEAMIENTO" },
  { v: "REGULARIZACION PARCELARIA", label: "REGULARIZACIÓN PARCELARIA" },
  { v: "ACTUALIZACION DE MENSURAS", label: "ACTUALIZACIÓN DE MENSURAS" },
];

const OP_VALUES = OPERACIONES.map((o) => o.v);

/**
 * Selector de hasta 3 operaciones/actuaciones técnicas concatenadas.
 * Persiste como un string único en `tipo_operacion`, separado por " Y ".
 * Ej: "DESLINDE Y SUBDIVISION".
 */
function OperacionesPicker({ value, onChange }) {
  // Parsear string entrante a array (split por " Y ")
  const initial = String(value || "DESLINDE").split(/\s+Y\s+/).filter(Boolean);
  // Tomar solo las que están en la lista (las demás se ignoran)
  const cleaned = initial.filter((v) => OP_VALUES.includes(v.toUpperCase()))
    .map((v) => v.toUpperCase());
  const ops = cleaned.length ? cleaned : ["DESLINDE"];

  const emit = (next) => {
    const filtered = next.filter(Boolean);
    onChange(filtered.length ? filtered.join(" Y ") : "DESLINDE");
  };
  const setAt = (idx, val) => {
    const next = [...ops];
    next[idx] = val;
    emit(next);
  };
  const addOp = () => {
    if (ops.length >= 3) return;
    const remaining = OPERACIONES.find((o) => !ops.includes(o.v));
    emit([...ops, remaining?.v || "SUBDIVISION"]);
  };
  const removeAt = (idx) => {
    if (ops.length <= 1) return;
    emit(ops.filter((_, i) => i !== idx));
  };

  const labels = ["Primera operación o actuación técnica",
                  "Segunda operación o actuación técnica",
                  "Tercera operación o actuación técnica"];

  return (
    <div className="md:col-span-2">
      <Label className="coord-label">OPERACIONES / ACTUACIONES TÉCNICAS</Label>
      <div className="mt-1.5 space-y-2">
        {ops.map((op, idx) => (
          <div key={idx} className="flex items-center gap-2" data-testid={`op-row-${idx}`}>
            <div className="flex-1">
              <Label className="text-xs text-slate-500 font-normal">{labels[idx]}</Label>
              <Select value={op} onValueChange={(v) => setAt(idx, v)}>
                <SelectTrigger data-testid={`f-tipo-op-${idx}`} className="mt-0.5 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {OPERACIONES.map((o) => (
                    <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {ops.length > 1 && (
              <Button
                data-testid={`op-del-${idx}`}
                type="button" variant="ghost" size="sm"
                onClick={() => removeAt(idx)}
                className="rounded-sm mt-5 text-red-500 hover:text-red-700 h-9"
                title="Quitar esta operación"
              >
                <Minus size={14} />
              </Button>
            )}
          </div>
        ))}
        {ops.length < 3 && (
          <Button
            data-testid="op-add-btn"
            type="button" variant="outline" size="sm"
            onClick={addOp}
            className="rounded-sm h-7"
          >
            <Plus size={12} className="mr-1" />
            {ops.length === 1 ? "Agregar segunda operación" : "Agregar tercera operación"}
          </Button>
        )}
      </div>
      <p className="text-xs text-slate-500 mt-1.5">
        Las operaciones se unirán con " Y " en los documentos (ej: "DESLINDE Y SUBDIVISIÓN").
      </p>
    </div>
  );
}

function Section({ title, children, hidden = false }) {
  if (hidden) return null;
  return (
    <div className="bg-white border border-slate-200">
      <div className="px-6 py-3 border-b border-slate-200">
        <h3 className="text-sm font-bold tracking-widest text-slate-900 uppercase">{title}</h3>
      </div>
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        {children}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testId, placeholder, mono = false, full = false, type = "text" }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="coord-label">{label}</Label>
      <Input
        data-testid={testId}
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1.5 rounded-sm ${mono ? "font-mono" : ""}`}
        placeholder={placeholder}
      />
    </div>
  );
}

function TextArea({ label, value, onChange, testId, full = true }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="coord-label">{label}</Label>
      <Textarea
        data-testid={testId}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 rounded-sm min-h-[80px]"
      />
    </div>
  );
}

export default function DatosTab({ exp, setExp, reload }) {
  const { t } = useLang();
  const { can } = useAuth();
  const isDuplicado = exp.tipo_tramite === "duplicado_perdida";
  const isPosesion = exp.tipo_tramite === "posesion_mejora";
  const hideAdvanced = isDuplicado || isPosesion;
  const upd = (group, field, val) => setExp({ ...exp, [group]: { ...exp[group], [field]: val } });
  const updNested = (group, sub, field, val) =>
    setExp({ ...exp, [group]: { ...exp[group], [sub]: { ...exp[group][sub], [field]: val } } });
  const updExp = (field, val) => setExp({ ...exp, [field]: val });

  return (
    <div className="space-y-6">
      {/* Auto-extracción del Oficio de Autorización */}
      {can("createExpediente") && (
        <div className="border-2 border-dashed border-orange-300 bg-orange-50 p-4 rounded-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="oficio-banner">
          <div>
            <div className="coord-label text-orange-700">// AUTOMÁTICO — MENSURAS CATASTRALES</div>
            <p className="text-sm font-semibold text-slate-900 mt-0.5">
              ¿Tienes el Oficio de Autorización que te entregó Mensuras?
            </p>
            <p className="text-xs text-slate-600">
              Súbelo y la IA extraerá automáticamente: número de expediente, propietario, parcela, D.C., área de derecho, fecha de vencimiento, etc.
            </p>
          </div>
          <OficioUpload expId={exp.id} onApplied={() => reload?.()} />
        </div>
      )}
      <Section title={t.section_general}>
        <Field label={t.numero_expediente} testId="f-numero-exp" value={exp.numero_expediente} onChange={(v) => updExp("numero_expediente", v)} mono full />
        <Field label={t.titulo_expediente} testId="f-titulo-exp" value={exp.titulo_expediente} onChange={(v) => updExp("titulo_expediente", v)} full />
        <OperacionesPicker
          value={exp.tipo_operacion || "DESLINDE"}
          onChange={(joined) => updExp("tipo_operacion", joined)}
        />
        <div>
          <Label className="coord-label">{t.drmc_departamento}</Label>
          <Select value={exp.drmc_departamento} onValueChange={(v) => updExp("drmc_departamento", v)}>
            <SelectTrigger data-testid="f-drmc-dpto" className="mt-1.5 rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Central">Central</SelectItem>
              <SelectItem value="Este">Este</SelectItem>
              <SelectItem value="Norte">Norte</SelectItem>
              <SelectItem value="Noroeste">Noroeste</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label className="coord-label">Tribunal de Tierras — Jurisdicción</Label>
          <Select
            value={
              ["Distrito Nacional","Santo Domingo","San Cristobal","Santiago","La Vega","Puerto Plata","San Pedro de Macoris","Higuey","San Juan","Monte Cristi","Barahona","Bonao","Azua","Salcedo","Mao","Cotui","Nagua","Bani"].includes(exp.tribunal_jurisdiccion)
                ? exp.tribunal_jurisdiccion
                : "otro"
            }
            onValueChange={(v) => {
              if (v === "otro") updExp("tribunal_jurisdiccion", "");
              else updExp("tribunal_jurisdiccion", v);
            }}
          >
            <SelectTrigger data-testid="f-tribunal" className="mt-1.5 rounded-sm"><SelectValue placeholder="Selecciona jurisdicción" /></SelectTrigger>
            <SelectContent>
              {["Distrito Nacional","Santo Domingo","San Cristobal","Santiago","La Vega","Puerto Plata","San Pedro de Macoris","Higuey","San Juan","Monte Cristi","Barahona","Bonao","Azua","Salcedo","Mao","Cotui","Nagua","Bani"].map(j => (
                <SelectItem key={j} value={j}>{j}</SelectItem>
              ))}
              <SelectItem value="otro">Otro (escribir abajo)</SelectItem>
            </SelectContent>
          </Select>
          <Input
            data-testid="f-tribunal-otro"
            value={exp.tribunal_jurisdiccion || ""}
            onChange={(e) => updExp("tribunal_jurisdiccion", e.target.value)}
            className="mt-2 rounded-sm"
            placeholder="Jurisdicción del Tribunal de Tierras..."
          />
        </div>
      </Section>

      <Section title={isDuplicado ? "Notario del Acto (Declaración Jurada y Compulsa)" : isPosesion ? "Notario del Acto (Declaración de Posesión y Compulsa)" : "Notario del Contrato y Solicitud"}>
        <Field label={t.not_nombre} testId="f-notp-nombre" value={exp.notario?.nombre} onChange={(v) => upd("notario", "nombre", v)} full />
        <Field label={t.not_matricula} testId="f-notp-matricula" value={exp.notario?.matricula} onChange={(v) => upd("notario", "matricula", v)} mono />
        <Field label={t.not_municipio + " (colegiatura)"} testId="f-notp-municipio" value={exp.notario?.municipio_colegio} onChange={(v) => upd("notario", "municipio_colegio", v)} placeholder="Distrito Nacional / Santo Domingo Oeste / ..." />
        <Field label="Provincia del Notario" testId="f-notp-provincia" value={exp.notario?.provincia} onChange={(v) => upd("notario", "provincia", v)} placeholder="Santo Domingo / San Cristóbal / La Vega / ..." />
        {!hideAdvanced && (
          <Field label="Municipio del acto (donde firma)" testId="f-notp-acto" value={exp.notario?.municipio_acto} onChange={(v) => upd("notario", "municipio_acto", v)} placeholder="Si es distinto al de colegiatura" />
        )}
        <div className="md:col-span-2 text-xs text-slate-500 -mt-2">
          {isDuplicado
            ? <>Este notario firma la <b>Declaración Jurada</b> y la <b>Compulsa</b> del trámite de Duplicado por Pérdida. El acto se firma en la ciudad y provincia del notario (su jurisdicción), no donde está el inmueble.</>
            : isPosesion
            ? <>Este notario firma la <b>Declaración Jurada de Posesión y Mejora</b> y su <b>Compulsa</b>. El acto se firma en la ciudad y provincia del notario (su jurisdicción), no donde está el inmueble.</>
            : <>Este notario es el que firma la <b>coletilla</b> al final del Contrato de Trabajo y la Solicitud de Autorización. Si la <b>colegiatura</b> es de un municipio (ej. Distrito Nacional) pero el <b>acto</b> se firma en otro (ej. Santo Domingo), llena ambos campos.</>}
        </div>
      </Section>

      <Section title={t.section_cliente}>
        <Field label={t.cliente_nombre} testId="f-cli-nombre" value={exp.cliente?.nombre} onChange={(v) => upd("cliente", "nombre", v)} full />
        <Field label={t.cliente_cedula} testId="f-cli-cedula" value={exp.cliente?.cedula} onChange={(v) => upd("cliente", "cedula", v)} mono />
        <div>
          <Label className="coord-label">{t.cliente_sexo}</Label>
          <Select value={exp.cliente?.sexo || "M"} onValueChange={(v) => upd("cliente", "sexo", v)}>
            <SelectTrigger data-testid="f-cli-sexo" className="mt-1.5 rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="M">Masculino</SelectItem>
              <SelectItem value="F">Femenino</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Field label={t.cliente_nacionalidad} testId="f-cli-nac" value={exp.cliente?.nacionalidad} onChange={(v) => upd("cliente", "nacionalidad", v)} />
        <Field label={t.cliente_estado_civil} testId="f-cli-estado" value={exp.cliente?.estado_civil} onChange={(v) => upd("cliente", "estado_civil", v)} />
        <Field label={t.cliente_profesion} testId="f-cli-profesion" value={exp.cliente?.profesion} onChange={(v) => upd("cliente", "profesion", v)} />
        <Field label={t.cliente_telefono} testId="f-cli-telefono" value={exp.cliente?.telefono} onChange={(v) => upd("cliente", "telefono", v)} />
        <Field label={t.cliente_email} testId="f-cli-email" value={exp.cliente?.email} onChange={(v) => upd("cliente", "email", v)} />
        <Field label={t.cliente_direccion} testId="f-cli-direccion" value={exp.cliente?.direccion} onChange={(v) => upd("cliente", "direccion", v)} full />
      </Section>

      {!hideAdvanced && (
        <>
          <Section title={t.section_acto_venta}>
            <Field label={t.av_numero} testId="f-av-numero" value={exp.acto_venta?.numero} onChange={(v) => upd("acto_venta", "numero", v)} />
            <Field label={t.av_fecha} testId="f-av-fecha" value={exp.acto_venta?.fecha} onChange={(v) => upd("acto_venta", "fecha", v)} placeholder="DD/MM/YYYY" />
            <Field label={t.av_vendedor} testId="f-av-vendedor" value={exp.acto_venta?.vendedor} onChange={(v) => upd("acto_venta", "vendedor", v)} />
            <Field label={t.av_comprador} testId="f-av-comprador" value={exp.acto_venta?.comprador} onChange={(v) => upd("acto_venta", "comprador", v)} />
            <Field label={t.av_precio} testId="f-av-precio" value={exp.acto_venta?.precio} onChange={(v) => upd("acto_venta", "precio", v)} />
          </Section>

          <Section title="Notario del Acto de Venta">
            <Field label={t.not_nombre} testId="f-not-nombre" value={exp.acto_venta?.notario?.nombre} onChange={(v) => updNested("acto_venta", "notario", "nombre", v)} full />
            <Field label={t.not_matricula} testId="f-not-matricula" value={exp.acto_venta?.notario?.matricula} onChange={(v) => updNested("acto_venta", "notario", "matricula", v)} mono />
            <Field label={t.not_municipio} testId="f-not-municipio" value={exp.acto_venta?.notario?.municipio_colegio} onChange={(v) => updNested("acto_venta", "notario", "municipio_colegio", v)} />
          </Section>
        </>
      )}

      <Section title={t.section_titulo}>
        <Field label={t.tit_numero} testId="f-tit-numero" value={exp.titulo?.numero_titulo} onChange={(v) => upd("titulo", "numero_titulo", v)} mono />
        <Field label={t.tit_matricula} testId="f-tit-matricula" value={exp.titulo?.matricula} onChange={(v) => upd("titulo", "matricula", v)} mono />
        <Field label="Libro" testId="f-tit-libro" value={exp.titulo?.libro} onChange={(v) => upd("titulo", "libro", v)} mono />
        <Field label="Folio" testId="f-tit-folio" value={exp.titulo?.folio} onChange={(v) => upd("titulo", "folio", v)} mono />
        <div className="md:col-span-2 p-3 bg-slate-50 border border-slate-200 rounded-sm">
          <Label className="coord-label">Tipo de Designación Catastral</Label>
          <Select
            value={exp.titulo?.tipo_designacion || "tradicional"}
            onValueChange={(v) => upd("titulo", "tipo_designacion", v)}
          >
            <SelectTrigger data-testid="f-tit-tipo-des" className="mt-1.5 rounded-sm bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tradicional">Tradicional (con D.C. y Parcela)</SelectItem>
              <SelectItem value="urbana">Urbana (Solar + Manzana, sin D.C.)</SelectItem>
              <SelectItem value="posicional">Posicional (provisional, sin D.C.)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {(exp.titulo?.tipo_designacion || "tradicional") === "urbana" ? (
          <>
            <Field label="Solar No." testId="f-tit-solar" value={exp.titulo?.solar} onChange={(v) => upd("titulo", "solar", v)} mono />
            <Field label="Manzana No." testId="f-tit-manzana" value={exp.titulo?.manzana} onChange={(v) => upd("titulo", "manzana", v)} mono />
          </>
        ) : (
          <>
            <Field label={t.tit_parcela} testId="f-tit-parcela" value={exp.titulo?.parcela} onChange={(v) => upd("titulo", "parcela", v)} mono />
            <Field label={t.tit_designacion} testId="f-tit-designacion" value={exp.titulo?.designacion_catastral} onChange={(v) => upd("titulo", "designacion_catastral", v)} mono />
          </>
        )}

        {/* D.C. ahora aplica tanto para tradicional como urbana (posicional puede omitir) */}
        {(exp.titulo?.tipo_designacion || "tradicional") !== "posicional" && (
          <Field label={t.tit_dc} testId="f-tit-dc" value={exp.titulo?.distrito_catastral} onChange={(v) => upd("titulo", "distrito_catastral", v)} mono />
        )}

        <Field label={t.tit_superficie} testId="f-tit-superficie" value={exp.titulo?.superficie} onChange={(v) => upd("titulo", "superficie", v)} placeholder="4,503.27 m²" />
        <Field label={t.tit_seccion} testId="f-tit-seccion" value={exp.titulo?.seccion} onChange={(v) => upd("titulo", "seccion", v)} />
        <Field label={t.tit_lugar} testId="f-tit-lugar" value={exp.titulo?.lugar_sitio} onChange={(v) => upd("titulo", "lugar_sitio", v)} />
        <Field label={t.tit_municipio} testId="f-tit-municipio" value={exp.titulo?.municipio} onChange={(v) => upd("titulo", "municipio", v)} />
        <Field label={t.tit_provincia} testId="f-tit-provincia" value={exp.titulo?.provincia} onChange={(v) => upd("titulo", "provincia", v)} />
        <Field label={t.tit_fecha} testId="f-tit-fecha" value={exp.titulo?.fecha_emision} onChange={(v) => upd("titulo", "fecha_emision", v)} placeholder="DD/MM/YYYY" />
      </Section>

      <Section title={t.section_terreno} hidden={hideAdvanced}>
        <Field label={t.ter_direccion} testId="f-ter-direccion" value={exp.terreno?.direccion} onChange={(v) => upd("terreno", "direccion", v)} full />
        <TextArea label={t.ter_referencias} testId="f-ter-referencias" value={exp.terreno?.referencias} onChange={(v) => upd("terreno", "referencias", v)} />
        <Field label={t.ter_coordenadas} testId="f-ter-coordenadas" value={exp.terreno?.coordenadas} onChange={(v) => upd("terreno", "coordenadas", v)} mono full placeholder="18.433065, -70.069702" />
        <Field label={t.ter_norte} testId="f-ter-norte" value={exp.terreno?.colindante_norte} onChange={(v) => upd("terreno", "colindante_norte", v)} />
        <Field label={t.ter_mat_norte} testId="f-ter-mat-norte" value={exp.terreno?.materializacion_norte} onChange={(v) => upd("terreno", "materializacion_norte", v)} placeholder="Pared, malla, alambre..." />
        <Field label={t.ter_sur} testId="f-ter-sur" value={exp.terreno?.colindante_sur} onChange={(v) => upd("terreno", "colindante_sur", v)} />
        <Field label={t.ter_mat_sur} testId="f-ter-mat-sur" value={exp.terreno?.materializacion_sur} onChange={(v) => upd("terreno", "materializacion_sur", v)} />
        <Field label={t.ter_este} testId="f-ter-este" value={exp.terreno?.colindante_este} onChange={(v) => upd("terreno", "colindante_este", v)} />
        <Field label={t.ter_mat_este} testId="f-ter-mat-este" value={exp.terreno?.materializacion_este} onChange={(v) => upd("terreno", "materializacion_este", v)} />
        <Field label={t.ter_oeste} testId="f-ter-oeste" value={exp.terreno?.colindante_oeste} onChange={(v) => upd("terreno", "colindante_oeste", v)} />
        <Field label={t.ter_mat_oeste} testId="f-ter-mat-oeste" value={exp.terreno?.materializacion_oeste} onChange={(v) => upd("terreno", "materializacion_oeste", v)} />
        <Field label={t.ter_tipo_construccion} testId="f-ter-tipo-construccion" value={exp.terreno?.tipo_construccion} onChange={(v) => upd("terreno", "tipo_construccion", v)} full />
      </Section>

      <Section title={t.section_honorarios} hidden={!can("seeHonorarios") || hideAdvanced}>
        <Field label={t.hon_moneda} testId="f-hon-moneda" value={exp.honorarios?.moneda} onChange={(v) => upd("honorarios", "moneda", v)} mono />
        <Field label={t.hon_fecha} testId="f-hon-fecha" value={exp.honorarios?.fecha_contrato} onChange={(v) => upd("honorarios", "fecha_contrato", v)} />

        <div className="md:col-span-2 p-3 bg-emerald-50 border border-emerald-200 rounded-sm">
          <Label className="coord-label">Monto TOTAL del contrato (suma de las dos cuotas)</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-1.5">
            <Input
              data-testid="f-hon-mt"
              value={exp.honorarios?.monto_total || ""}
              onChange={(e) => {
                const v = e.target.value;
                const next = { ...exp.honorarios, monto_total: v };
                const auto = numberToSpanishPesos(v);
                if (auto) next.monto_total_letras = auto;
                setExp({ ...exp, honorarios: next });
              }}
              placeholder="90000.00"
              className="rounded-sm font-mono"
            />
            <Input
              data-testid="f-hon-mtl"
              value={exp.honorarios?.monto_total_letras || ""}
              onChange={(e) => upd("honorarios", "monto_total_letras", e.target.value)}
              placeholder="NOVENTA MIL PESOS DOMINICANOS"
              className="rounded-sm"
            />
          </div>
          {(() => {
            const m1 = parseFloat(String(exp.honorarios?.monto_1 || "0").replace(/,/g,""));
            const m2 = parseFloat(String(exp.honorarios?.monto_2 || "0").replace(/,/g,""));
            const mt = parseFloat(String(exp.honorarios?.monto_total || "0").replace(/,/g,""));
            const sum = (Number.isFinite(m1)?m1:0) + (Number.isFinite(m2)?m2:0);
            if (mt > 0 && Math.abs(sum - mt) > 0.01) {
              return <div className="mt-2 text-xs text-amber-700">⚠ La suma de las cuotas ({sum.toFixed(2)}) no coincide con el total ({mt.toFixed(2)})</div>;
            }
            return null;
          })()}
        </div>

        <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          <Input
            data-testid="f-hon-m1"
            value={exp.honorarios?.monto_1 || ""}
            onChange={(e) => {
              const v = e.target.value;
              const next = { ...exp.honorarios, monto_1: v };
              const auto = numberToSpanishPesos(v);
              if (auto) next.monto_1_letras = auto;
              setExp({ ...exp, honorarios: next });
            }}
            placeholder="45000.00"
            className="rounded-sm font-mono"
          />
          <Input
            data-testid="f-hon-m1l"
            value={exp.honorarios?.monto_1_letras || ""}
            onChange={(e) => upd("honorarios", "monto_1_letras", e.target.value)}
            placeholder="1ra cuota en letras"
            className="rounded-sm"
          />
        </div>
        <Field label={t.hon_condicion_1} testId="f-hon-c1" value={exp.honorarios?.condicion_1} onChange={(v) => upd("honorarios", "condicion_1", v)} full />
        <div className="flex items-center gap-2 md:col-span-2">
          <Checkbox
            data-testid="f-hon-p1"
            checked={!!exp.honorarios?.monto_1_pagado}
            onCheckedChange={(v) => upd("honorarios", "monto_1_pagado", !!v)}
            id="p1"
          />
          <Label htmlFor="p1" className="text-sm cursor-pointer">{t.hon_pagado_1}</Label>
        </div>

        <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          <Input
            data-testid="f-hon-m2"
            value={exp.honorarios?.monto_2 || ""}
            onChange={(e) => {
              const v = e.target.value;
              const next = { ...exp.honorarios, monto_2: v };
              const auto = numberToSpanishPesos(v);
              if (auto) next.monto_2_letras = auto;
              setExp({ ...exp, honorarios: next });
            }}
            placeholder="45000.00"
            className="rounded-sm font-mono"
          />
          <Input
            data-testid="f-hon-m2l"
            value={exp.honorarios?.monto_2_letras || ""}
            onChange={(e) => upd("honorarios", "monto_2_letras", e.target.value)}
            placeholder="2da cuota en letras"
            className="rounded-sm"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="coord-label">{t.hon_condicion_2}</Label>
          <Select
            value={
              ["con el oficio de aprobación de la Dirección Regional de Mensuras Catastrales",
               "hasta la obtención del Certificado de Título"].includes(exp.honorarios?.condicion_2)
                ? exp.honorarios.condicion_2
                : "otro"
            }
            onValueChange={(v) => {
              if (v === "otro") upd("honorarios", "condicion_2", "");
              else upd("honorarios", "condicion_2", v);
            }}
          >
            <SelectTrigger data-testid="f-hon-c2-preset" className="mt-1.5 rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="con el oficio de aprobación de la Dirección Regional de Mensuras Catastrales">
                Con aprobación de la DRMC
              </SelectItem>
              <SelectItem value="hasta la obtención del Certificado de Título">
                Hasta la obtención del Título
              </SelectItem>
              <SelectItem value="otro">Otro (escribir abajo)</SelectItem>
            </SelectContent>
          </Select>
          <Input
            data-testid="f-hon-c2"
            value={exp.honorarios?.condicion_2 || ""}
            onChange={(e) => upd("honorarios", "condicion_2", e.target.value)}
            className="mt-2 rounded-sm"
            placeholder="Texto personalizado de la condición..."
          />
        </div>
        <div className="flex items-center gap-2 md:col-span-2">
          <Checkbox
            data-testid="f-hon-p2"
            checked={!!exp.honorarios?.monto_2_pagado}
            onCheckedChange={(v) => upd("honorarios", "monto_2_pagado", !!v)}
            id="p2"
          />
          <Label htmlFor="p2" className="text-sm cursor-pointer">{t.hon_pagado_2}</Label>
        </div>
        <Field label={t.hon_municipio} testId="f-hon-muni" value={exp.honorarios?.municipio_contrato} onChange={(v) => upd("honorarios", "municipio_contrato", v)} full placeholder="Santo Domingo, Distrito Nacional" />
      </Section>
    </div>
  );
}
